import {
  getTicketModel, isEarlyArrival, isEarlyLoose, isGeneral, isVip, isComp,
  hasFemaleKeyword, hasMaleKeyword,
} from './ticketModel';
import { getVenueTz } from './dashboardContext';

// Normalize Tixr tier names to match Universe conventions for gender/tier detection
// "Female GA Tier 1" → "Female General Admission Tier 1"
// "Male GA (Admission before 1AM)" → "Male General Admission (Admission before 1AM)"
export const normalizeTierName = (name) => {
  if (!name) return name;
  // Only expand "GA" when it's the shorthand, not part of another word
  return name.replace(/\bGA\b/g, 'General Admission');
};

// Helper function to safely parse price
export const parsePrice = (priceStr) => {
  if (!priceStr || priceStr === '-') return 0;
  
  // If it's already a number, return it as-is
  if (typeof priceStr === 'number') return priceStr;
  
  // Otherwise parse the string
  const cleaned = priceStr.toString().replace(/[^0-9.]/g, '');
  return parseFloat(cleaned) || 0;
};

// Helper function to get lowest price with display price
export const getLowestPriceWithDisplay = (rates, availability, gender, exclude = null, startswith = null) => {
  if (!rates || !availability) return { base: '-', display: '-' };
  
  const validRates = [];
  
  for (const rate of rates) {
    const name = rate.name;
    if (!name.includes(gender)) continue;
    if (exclude && name.includes(exclude)) continue;
    if (startswith && !name.startsWith(startswith)) continue;
    
    const avail = availability.find(a => a.rateId === rate.id)?.countAvailable ?? -1;
    if (avail !== 0) {
      validRates.push({ 
        price: rate.price, 
        displayPrice: rate.displayPrice || rate.price,
        avail 
      });
    }
  }
  
  if (validRates.length === 0) return { base: '-', display: '-' };
  
  // Prioritize tiers with actual availability (>0), else lowest price
  const availableRates = validRates.filter(r => r.avail > 0);
  const ratesToUse = availableRates.length > 0 ? availableRates : validRates;
  
  // Find the rate with the lowest base price
  const lowestRate = ratesToUse.reduce((min, rate) => 
    rate.price < min.price ? rate : min
  );
  
  return {
    base: lowestRate.price.toString(),
    display: lowestRate.displayPrice.toString()
  };
};

// Match a dashboard event to its Meta campaigns via the server-computed
// campaign→event match (campaign.matched_event, persisted by the meta poller:
// manual override → destination-URL → name-date). The dashboard's events are
// Universe GraphQL nodes, so the join key is matched_event.universe_id ===
// event.id — exact, and unlike the old date-in-name matching it doesn't
// cross-attach campaigns when two events share a date. matched_event.date is
// the fallback for events with no Universe publication.
export const matchEventWithCampaigns = (event, metaCampaigns) => {
  if (!metaCampaigns || metaCampaigns.length === 0) return [];

  // Payload from a server that doesn't send matched_event yet (deploy
  // transition) → legacy date-in-name matching.
  if (!metaCampaigns.some(c => c.matched_event !== undefined)) {
    return legacyMatchByCampaignNameDate(event, metaCampaigns);
  }

  return metaCampaigns.filter(campaign => {
    const m = campaign.matched_event;
    if (!m) return false; // unmatched or general/brand campaign
    if (m.universe_id) return m.universe_id === event.id;
    return !!m.date && m.date === event.date;
  });
};

// Pre-matched-event fallback: date extracted from the campaign name + word
// overlap for "general" campaigns. Kept only for payloads from an older
// server; cross-attaches campaigns when two events share a date.
const legacyMatchByCampaignNameDate = (event, metaCampaigns) => {
  const eventDate = new Date(event.date + 'T00:00:00');
  const eventTitle = (event.title || '').toLowerCase();

  return metaCampaigns.filter(campaign => {
    const campaignName = campaign.name.toLowerCase();
    const campaignDate = extractDateFromCampaignName(campaign.name);
    if (campaignDate) return isSameDate(campaignDate, eventDate);
    if (campaignName.includes('general')) {
      const eventWords = eventTitle.split(/\s+/).filter(word => word.length > 2);
      return eventWords.some(word => campaignName.includes(word));
    }
    return false;
  });
};

// Helper function to extract date from campaign name (e.g., "07.30.25 - LIV - Chris Brown")
export const extractDateFromCampaignName = (campaignName) => {
  const dateMatch = campaignName.match(/(\d{2})\.(\d{2})\.(\d{2})/);
  if (dateMatch) {
    const [, month, day, year] = dateMatch;
    return new Date(2000 + parseInt(year), parseInt(month) - 1, parseInt(day));
  }
  return null;
};

// Helper function to check if two dates are the same day
export const isSameDate = (date1, date2) => {
  if (!date1 || !date2) return false;
  return date1.getFullYear() === date2.getFullYear() &&
         date1.getMonth() === date2.getMonth() &&
         date1.getDate() === date2.getDate();
};

// Today's calendar date in the VENUE's timezone as YYYY-MM-DD. Day-boundary
// logic must never use the browser's TZ — a wall display or phone outside the
// venue's zone would flip "Today" at the wrong midnight. (en-CA = YYYY-MM-DD.)
export const venueTodayStr = () => {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: getVenueTz() }).format(new Date());
  } catch {
    return new Date().toISOString().split('T')[0];
  }
};

// Calculate days until event — venue-TZ day math (event.date is already the
// venue-local calendar date; compare it to the venue's "today", not the
// browser's). UTC-noon anchors make the subtraction DST-proof.
export const calculateDaysUntil = (eventDate) => {
  if (!eventDate || eventDate === 'TBD') return null;

  const event = new Date(eventDate + 'T12:00:00Z');
  const today = new Date(venueTodayStr() + 'T12:00:00Z');
  const diffDays = Math.round((event - today) / (1000 * 60 * 60 * 24));

  return diffDays;
};

// Format days until message
export const formatDaysUntil = (days) => {
  if (days === null) return null;
  if (days < -30) return 'Past';
  if (days < 0) return `${Math.abs(days)}d ago`;
  if (days === 0) return 'Today';
  if (days === 1) return '1d';
  if (days <= 30) return `${days}d`;
  if (days <= 60) return `${Math.round(days / 7)}w`;
  return `${Math.round(days / 30)}mo`;
};

// Helper to extract entry type from parentheses
const extractEntryType = (tierName) => {
  const match = tierName.match(/\(([^)]+)\)/);
  return match ? match[1] : null;
};

// Detect ticket structure type from tier names
// WeakMap memo: structure detection runs per card per render, and
// calculateMetrics calls it again internally — same tierSales object in a
// render pass means same result. New payloads are new objects → cache misses.
const _structureCache = new WeakMap();
export const detectTicketStructure = (tierSales) => {
  if (tierSales && typeof tierSales === 'object' && _structureCache.has(tierSales)) {
    return _structureCache.get(tierSales);
  }
  const result = _detectTicketStructure(tierSales);
  if (tierSales && typeof tierSales === 'object') _structureCache.set(tierSales, result);
  return result;
};

const _detectTicketStructure = (tierSales) => {
  if (!tierSales || Object.keys(tierSales).length === 0) {
    return {
      type: 'none',
      hasEarlyTiers: false,
      hasGeneralTiers: false,
      hasGenderSplit: false,
      hasVIP: false,
      hasComp: false,
      earlyTiers: [],
      generalTiers: [],
      vipTiers: [],
      compTiers: [],
      otherTiers: [],
      tieredGroups: {}
    };
  }

  const tierNames = Object.keys(tierSales);
  const normalizedNames = tierNames.map(n => normalizeTierName(n));
  const lowerNames = normalizedNames.map(n => n.toLowerCase());

  // Detect patterns via the per-tenant ticket model's vocabulary (LIV default
  // reproduces the source keywords; Tixr "GA" → "General Admission" via
  // normalizeTierName). hasMaleKeyword preserves the source quirk that
  // "female" contains "male".
  const m = getTicketModel();
  const hasEarlyArrival = lowerNames.some(n => isEarlyArrival(n, m));
  const hasGeneralAdmission = lowerNames.some(n => isGeneral(n, m));
  const hasFemale = lowerNames.some(n => hasFemaleKeyword(n, m));
  const hasMale = lowerNames.some(n => hasMaleKeyword(n, m));
  const hasVIP = lowerNames.some(n => isVip(n, m));
  const hasComp = lowerNames.some(n => isComp(n, m));

  // Categorize tiers
  const earlyTiers = [];
  const generalTiers = [];
  const vipTiers = [];
  const compTiers = [];
  const otherTiers = [];

  tierNames.forEach((tierName) => {
    const lower = normalizeTierName(tierName).toLowerCase();

    if (isComp(lower, m)) {
      compTiers.push(tierName);
    } else if (isVip(lower, m)) {
      vipTiers.push(tierName);
    } else if (isEarlyArrival(lower, m) || (isEarlyLoose(lower, m) && !lower.includes('general admission tier'))) {
      earlyTiers.push(tierName);
    } else if (isGeneral(lower, m)) {
      generalTiers.push(tierName);
    } else {
      otherTiers.push(tierName);
    }
  });

  // Determine structure type
  let type = 'custom';
  if (hasEarlyArrival && hasGeneralAdmission && (hasFemale || hasMale)) {
    type = 'classic'; // Early/General with gender splits
  } else if (hasEarlyArrival || hasGeneralAdmission) {
    type = 'partial-classic'; // Has early/general but maybe no gender split
  } else if (otherTiers.length > 0) {
    type = 'custom'; // Uses different naming scheme
  }

  // Group tiers by category + gender + entry type for non-classic events
  const tieredGroups = {};
  if (type !== 'classic') {
    tierNames.forEach(tierName => {
      const lower = tierName.toLowerCase();

      // Skip VIP and COMP tiers
      if (lower.includes('vip') || lower.includes('comp')) return;

      // Extract category (text before gender, often separated by dash or space)
      let category = null;
      const categoryMatch = tierName.match(/^([^-]+?)\s*-\s*(Female|Male|Men)/i);
      if (categoryMatch) {
        category = categoryMatch[1].trim();
      }

      // Determine gender (model vocabulary; female tested before male since
      // "female" contains "male")
      let gender = null;
      if (hasFemaleKeyword(lower, m)) gender = 'Female';
      else if (hasMaleKeyword(lower, m)) gender = 'Male';

      // Extract entry type from parentheses
      const entryType = extractEntryType(tierName);

      if (gender && entryType) {
        const groupKey = category
          ? `${category}|${gender}|${entryType}`
          : `${gender}|${entryType}`;

        if (!tieredGroups[groupKey]) {
          tieredGroups[groupKey] = {
            category,
            gender,
            entryType,
            tiers: [],
            minPrice: Infinity,
            maxPrice: -Infinity,
            tierCount: 0,
            currentPrice: null,
            currentTierName: null,
            allSoldOut: true
          };
        }

        const tierData = tierSales[tierName];
        const price = tierData.price || 0;
        const available = tierData.maxQuantity > 0 ? tierData.maxQuantity - tierData.sold : -1;

        tieredGroups[groupKey].tiers.push({
          name: tierName,
          price: price,
          available: available,
          sortIndex: tierData.sortIndex || 0
        });
        tieredGroups[groupKey].tierCount++;

        if (price > 0) {
          tieredGroups[groupKey].minPrice = Math.min(tieredGroups[groupKey].minPrice, price);
          tieredGroups[groupKey].maxPrice = Math.max(tieredGroups[groupKey].maxPrice, price);
        }

        // Track if any tier is available
        if (available !== 0) {
          tieredGroups[groupKey].allSoldOut = false;
        }
      }
    });

    // For each group, find the current available tier (lowest priced tier with availability)
    Object.keys(tieredGroups).forEach(key => {
      const group = tieredGroups[key];

      // Clean up infinity values
      if (group.minPrice === Infinity) {
        group.minPrice = 0;
      }
      if (group.maxPrice === -Infinity) {
        group.maxPrice = 0;
      }

      // Sort tiers by sortIndex (which Universe uses to order tiers)
      group.tiers.sort((a, b) => a.sortIndex - b.sortIndex);

      // Find the first tier with availability > 0
      const activeTier = group.tiers.find(t => t.available > 0);

      if (activeTier) {
        group.currentPrice = activeTier.price;
        group.currentTierName = activeTier.name;
      } else if (!group.allSoldOut) {
        // If no tier has known availability, use the first tier (unknown availability)
        const unknownAvailTier = group.tiers.find(t => t.available === -1);
        if (unknownAvailTier) {
          group.currentPrice = unknownAvailTier.price;
          group.currentTierName = unknownAvailTier.name;
        }
      }
    });
  }

  return {
    type,
    hasEarlyTiers: earlyTiers.length > 0,
    hasGeneralTiers: generalTiers.length > 0,
    hasGenderSplit: hasFemale || hasMale,
    hasVIP: vipTiers.length > 0,
    hasComp: compTiers.length > 0,
    earlyTiers,
    generalTiers,
    vipTiers,
    compTiers,
    otherTiers,
    tieredGroups
  };
};

// Calculate metrics
export const calculateMetrics = (event) => {
  let totalFemale = 0;
  let totalMale = 0;
  let totalVIP = 0;
  let totalComp = 0;
  let totalOther = 0;

  // If we have tierSales data, use it (more accurate)
  if (event.tierSales && Object.keys(event.tierSales).length > 0) {
    Object.entries(event.tierSales).forEach(([tierName, tierData]) => {
      const name = tierName.toLowerCase();
      const sold = tierData.sold || 0;

      // Classify via the per-tenant ticket-model matchers (NOT hardcoded
      // English literals). Tiers with no gender signal go to `totalOther` —
      // the old 50/50 split padded both sides and dragged every displayed
      // ratio toward 50:50 (fabricated data).
      if (hasFemaleKeyword(name)) {
        totalFemale += sold;
      } else if (hasMaleKeyword(name)) {
        totalMale += sold;
      } else if (isVip(name)) {
        totalVIP += sold;
      } else if (isComp(name)) {
        totalComp += sold;
      } else {
        totalOther += sold;
      }
    });
  } else {
    // Fallback to old tickets structure if tierSales not available
    totalFemale = event.tickets.earlyFemale.sold + event.tickets.generalFemale.sold;
    totalMale = event.tickets.earlyMale.sold + event.tickets.generalMale.sold;
    totalVIP = event.tickets.vip.sold;
    totalComp = event.tickets.comp.sold;
  }

  // Calculate totalEarly adaptively
  let totalEarly = 0;

  // First, try using tierSales data (more accurate and adaptive)
  if (event.tierSales && Object.keys(event.tierSales).length > 0) {
    const structure = detectTicketStructure(event.tierSales);

    // Sum up all early tiers
    structure.earlyTiers.forEach(tierName => {
      totalEarly += event.tierSales[tierName]?.sold || 0;
    });
  } else {
    // Fallback to old tickets structure if tierSales not available
    totalEarly = (event.tickets?.earlyFemale?.sold || 0) + (event.tickets?.earlyMale?.sold || 0);
  }

  const total = totalFemale + totalMale + totalVIP + totalComp + totalOther;

  // Ratio from gender-MATCHED tiers only; null when nothing matched (the UI
  // hides the bar rather than inventing a split).
  let ratio = null;
  if (totalFemale + totalMale > 0) {
    const femalePct = Math.round((totalFemale / (totalFemale + totalMale)) * 100);
    const malePct = 100 - femalePct;
    ratio = `${femalePct}:${malePct}`;
  }

  const soldPct = event.capacity > 0
    ? Math.round((event.sold / event.capacity) * 100)
    : 0;

  const avgPrice = event.sold > 0 ? Math.round(event.revenue / event.sold) : 0;

  // Check if event is waitlisted — only meaningful when a real capacity is
  // known (capacity 0 = uncapped/unknown; -sold < 1 flagged EVERY such event).
  const availableTickets = event.capacity - event.sold;
  const isWaitlisted = event.capacity > 0 && availableTickets < 1;

  return {
    totalFemale,
    totalMale,
    totalOther,
    totalEarly,
    total,
    ratio,
    soldPct,
    avgPrice,
    isWaitlisted,
    availableTickets
  };
};

// Formatting helpers
export const formatCurrency = (amount) => {
  if (amount === undefined || amount === null) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

export const formatCurrencyWithDecimals = (amount) => {
  if (amount === undefined || amount === null) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

export const formatCurrencyCompact = (amount) => {
  if (amount === undefined || amount === null) return '$0';
  if (amount >= 1000000) {
    return `$${(amount / 1000000).toFixed(1)}M`;
  }
  if (amount >= 10000) {
    return `$${Math.round(amount / 1000)}K`;
  }
  if (amount >= 1000) {
    return `$${(amount / 1000).toFixed(1)}K`;
  }
  return formatCurrency(amount);
};

export const formatNumber = (num) => {
  if (num === undefined || num === null) return '0';
  return new Intl.NumberFormat('en-US').format(num);
};

export const formatNumberCompact = (num) => {
  if (num === undefined || num === null) return '0';
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  }
  if (num >= 10000) {
    return `${Math.round(num / 1000)}K`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }
  return num.toString();
};

export const formatPercentage = (num) => {
  if (num === undefined || num === null) return '0%';
  return `${parseFloat(num).toFixed(1)}%`;
};

export const formatConversionTypeName = (type) => {
  const typeMap = {
    'purchase': 'Purchase',
    'complete_registration': 'Registration',
    'lead': 'Lead',
    'initiate_checkout': 'Checkout Started',
    'omni_initiated_checkout': 'Checkout Started',
    'onsite_web_initiate_checkout': 'Web Checkout',
    'offsite_conversion.fb_pixel_initiate_checkout': 'Pixel Checkout',
    'view_content': 'Content Views',
    'omni_view_content': 'Content Views',
    'onsite_web_view_content': 'Web Views',
    'onsite_web_app_view_content': 'App Views',
    'offsite_conversion.fb_pixel_view_content': 'Pixel Views',
    'add_to_cart': 'Add to Cart',
    'add_to_wishlist': 'Add to Wishlist',
    'onsite_conversion.post_save': 'Saved Posts',
    'landing_page_view': 'Landing Views',
    'omni_landing_page_view': 'Landing Views',
    'offsite_conversion.custom': 'Custom'
  };
  
  return typeMap[type] || type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

export const formatDate = (dateStr) => {
  if (!dateStr || dateStr === 'TBD') {
    return { dayName: 'TBD', month: '', day: '', year: '' };
  }
  // Parse the date string and ensure we're using the correct date
  // If the date string includes time, extract just the date part
  const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
  const date = new Date(datePart + 'T00:00:00'); // Add time to avoid timezone issues
  const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const day = date.getDate();
  const year = date.getFullYear();
  return { dayName, month, day, year };
};

// Real instants → render in the VENUE timezone (default from dashboardContext),
// with the zone abbrev (e.g. EDT) so it's unambiguous — never the browser's tz.
export const formatTime = (date, tz = getVenueTz()) => {
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit', hour12: true,
    timeZone: tz, timeZoneName: 'short',
  });
};

export const formatDateTime = (dateTimeStr, tz = getVenueTz()) => {
  if (!dateTimeStr) return '—';
  const date = new Date(dateTimeStr);
  return date.toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
    timeZone: tz, timeZoneName: 'short',
  });
};

// Helper function to extract conversion data
export const getConversionData = (insights) => {
  if (!insights || !insights.spend) {
    return {
      conversions: {},
      totalConversionValue: 0,
      totalPurchaseValue: 0,
      roas: 0,
      purchaseROAS: 0,
      websiteROAS: 0
    };
  }

  const actions = insights.actions || [];
  const costPerActionType = insights.cost_per_action_type || [];
  const actionValues = insights.action_values || [];
  const conversionValues = insights.conversion_values || [];
  const purchaseRoas = insights.purchase_roas || [];
  const websitePurchaseRoas = insights.website_purchase_roas || [];
  
  const conversions = {};
  let totalPurchaseValue = 0;
  let hasActualValues = false;
  
  // Track which action types we've already counted to avoid duplicates
  const countedValueTypes = new Set();
  
  // Extract conversion counts
  actions.forEach(action => {
    const actionType = action.action_type;
    conversions[actionType] = {
      count: parseInt(action.value || 0),
      cost: 0,
      value: 0,
      isEstimated: false
    };
  });
  
  // Extract cost per conversion
  costPerActionType.forEach(cost => {
    const actionType = cost.action_type;
    if (conversions[actionType]) {
      conversions[actionType].cost = parseFloat(cost.value || 0);
    }
  });
  
  // Extract conversion values from action_values (PRIMARY SOURCE)
  actionValues.forEach(value => {
    const actionType = value.action_type;
    const conversionValue = parseFloat(value.value || 0);
    
    if (conversions[actionType] && !countedValueTypes.has(actionType)) {
      conversions[actionType].value = conversionValue;
      hasActualValues = true;
      countedValueTypes.add(actionType);
      
      // Only add to total if not already counted
      if (actionType === 'purchase' || actionType === 'omni_purchase') {
        totalPurchaseValue = conversionValue; // Don't accumulate, just set
      }
    }
  });
  
  // If we already have purchase value from action_values, don't add from other sources
  if (totalPurchaseValue === 0) {
    // Check conversion_values array only if we haven't found purchase value yet
    conversionValues.forEach(value => {
      const actionType = value.action_type;
      const conversionValue = parseFloat(value.value || 0);
      
      if ((actionType === 'purchase' || actionType === 'omni_purchase') && !countedValueTypes.has(actionType)) {
        totalPurchaseValue = conversionValue;
        if (conversions[actionType]) {
          conversions[actionType].value = conversionValue;
        }
        hasActualValues = true;
        countedValueTypes.add(actionType);
      }
    });
  }
  
  // Get ROAS from different sources
  let roas = 0;
  let purchaseROAS = 0;
  let websiteROAS = 0;
  
  // Check purchase_roas array
  if (purchaseRoas.length > 0) {
    const purchaseRoasData = purchaseRoas.find(r => r.action_type === 'purchase' || r.action_type === 'omni_purchase');
    if (purchaseRoasData) {
      purchaseROAS = parseFloat(purchaseRoasData.value || 0);
      roas = purchaseROAS;
    }
  }
  
  // Check website_purchase_roas
  if (websitePurchaseRoas.length > 0) {
    const websiteRoasData = websitePurchaseRoas.find(r => r.action_type === 'purchase' || r.action_type === 'omni_purchase');
    if (websiteRoasData) {
      websiteROAS = parseFloat(websiteRoasData.value || 0);
      if (!roas) roas = websiteROAS;
    }
  }
  
  // Calculate ROAS if not provided (but we have the calculated value now)
  if (!roas && totalPurchaseValue > 0 && insights.spend > 0) {
    roas = totalPurchaseValue / parseFloat(insights.spend);
  }
  
  // If we have ROAS but no purchase value, calculate it
  if (roas > 0 && totalPurchaseValue === 0 && insights.spend > 0) {
    totalPurchaseValue = parseFloat(insights.spend) * roas;
    if (conversions.purchase) {
      conversions.purchase.value = totalPurchaseValue;
    }
  }
  
  return {
    conversions,
    totalConversionValue: totalPurchaseValue, // Use purchase value as total
    totalPurchaseValue,
    roas,
    purchaseROAS,
    websiteROAS,
    hasEstimatedValues: !hasActualValues && actionValues.length === 0
  };
};